import { Group, LinearFilter, LinearMipmapLinearFilter, SRGBColorSpace, type ColorRepresentation } from "three";
import { useEffect, useMemo, useRef } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import { RoundedBox, useTexture } from "@react-three/drei";
import { Color } from "three";
import type { ProjectCarouselEntry } from "../../../data/projects";
import { projects } from "../../../data/projects";
import { getCircularDelta } from "../../../lib/projectsCarousel";
import type { ViewportTier } from "../../../lib/viewport";
import { isMobileViewport } from "../../../lib/viewport";

type ProjectCard3DProps = {
  project: ProjectCarouselEntry;
  index: number;
  rotationProgress: number;
  weight: number;
  compact: boolean;
  viewport: ViewportTier;
};

export function ProjectCard3D({ compact, index, project, rotationProgress, viewport, weight }: ProjectCard3DProps) {
  const groupRef = useRef<Group>(null);
  const primary = useMemo(() => new Color(project.palette.primary), [project.palette.primary]);
  const secondary = useMemo(() => new Color(project.palette.secondary), [project.palette.secondary]);
  const texture = useTexture(project.imageSrc);
  const gl = useThree((state) => state.gl);
  const mobile = isMobileViewport(viewport);

  useEffect(() => {
    texture.colorSpace = SRGBColorSpace;
    texture.generateMipmaps = true;
    texture.minFilter = LinearMipmapLinearFilter;
    texture.magFilter = LinearFilter;
    texture.anisotropy = Math.min(8, gl.capabilities.getMaxAnisotropy());
    texture.needsUpdate = true;
  }, [gl, texture]);

  const renderClamp = mobile ? 1.15 : 1.35;
  const renderLayoutDelta = Math.max(-renderClamp, Math.min(renderClamp, getCircularDelta(index, rotationProgress, projects.length)));
  const renderAbsDelta = Math.min(Math.abs(renderLayoutDelta), renderClamp);
  const renderProminence = Math.max(0.3, 1 - renderAbsDelta * (mobile ? 0.4 : compact ? 0.38 : 0.42));

  useFrame((_, delta) => {
    if (!groupRef.current) {
      return;
    }

    const circularDelta = getCircularDelta(index, rotationProgress, projects.length);
    const layoutClamp = mobile ? 1.15 : 1.35;
    const layoutDelta = Math.max(-layoutClamp, Math.min(layoutClamp, circularDelta));
    const absDelta = Math.min(Math.abs(layoutDelta), layoutClamp);
    const side = Math.sign(layoutDelta);
    const prominence = Math.max(0.3, 1 - absDelta * (mobile ? 0.4 : compact ? 0.38 : 0.42));
    const x = mobile
      ? layoutDelta * 0.78
      : compact
        ? layoutDelta * 1.2
        : layoutDelta * 1.62 + side * 0.24;
    const z = mobile
      ? -1.02 - absDelta * 0.95
      : compact
        ? -1.48 - absDelta * 0.82
        : -1.12 - absDelta * 0.96;
    const y = mobile
      ? -0.02 - absDelta * 0.035
      : compact
        ? -0.04 - absDelta * 0.065
        : -0.02 - absDelta * 0.075;
    const yaw = side === 0 ? (mobile ? -0.01 : -0.5) : -side * (mobile ? 0.18 : compact ? 0.5 : 0.6);
    const roll = side === 0 ? (mobile ? 0 : -0.006) : side * (mobile ? 0.035 : 0.5);

    groupRef.current.position.x += (x - groupRef.current.position.x) * (1 - Math.exp(-delta * 7.2));
    groupRef.current.position.y += (y - groupRef.current.position.y) * (1 - Math.exp(-delta * 7.2));
    groupRef.current.position.z += (z - groupRef.current.position.z) * (1 - Math.exp(-delta * 7.2));
    groupRef.current.rotation.y += (yaw - groupRef.current.rotation.y) * (1 - Math.exp(-delta * 7.2));
    groupRef.current.rotation.z += (roll - groupRef.current.rotation.z) * (1 - Math.exp(-delta * 7.2));

    const targetScale = mobile
      ? 0.5 + prominence * 0.46
      : compact
        ? 0.44 + prominence * 0.58
        : 0.52 + prominence * 0.58;
    groupRef.current.scale.x += (targetScale - groupRef.current.scale.x) * (1 - Math.exp(-delta * 6.4));
    groupRef.current.scale.y += (targetScale - groupRef.current.scale.y) * (1 - Math.exp(-delta * 6.4));
    groupRef.current.scale.z += (targetScale - groupRef.current.scale.z) * (1 - Math.exp(-delta * 6.4));

    groupRef.current.visible = prominence * weight > 0.02;
  });

  return (
    <group ref={groupRef}>
      <mesh
        castShadow
        receiveShadow
        position={[0, 0.04, 0.04]}
        scale={mobile ? [1.08, 1.62, 1] : compact ? [1.26, 1.82, 1] : [1.56, 2.18, 1]}
      >
        <RoundedBox args={[1.2, 1.2, 0.1]} radius={0.07} smoothness={10}>
          <meshStandardMaterial
            color="#fffefb"
            roughness={0.12}
            metalness={0.06}
            envMapIntensity={1.4}
            emissive={secondary as unknown as ColorRepresentation}
            emissiveIntensity={(0.01 + renderProminence * 0.015) * weight}
            opacity={0.98 * weight}
            transparent
          />
        </RoundedBox>

      </mesh>

      <mesh
        castShadow
        receiveShadow
        position={[0, 0.04, 0.14]}
        scale={mobile ? [0.96, 1.48, 1] : compact ? [1.05, 1.56, 1] : [1.4, 2, 1]}
      >
        <planeGeometry args={[1.2, 1.2]} />
        <meshStandardMaterial
          color="#ffffff"
          map={texture}
          roughness={0.72}
          metalness={0}
          emissive="#000000"
          emissiveIntensity={0}
          toneMapped={false}
        />
      </mesh>
    </group>
  );
}
